import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { AddBriefDemographicsDto, InfluencerTypeEnum } from '@/app/common/general-types/briefs.dto';
import { FOLLOWER_RANGES } from '@/app/pages/create-brief-select/brief-builder.constants';
import { BbSwitch } from '@/app/pages/create-brief-select/ui/bb-switch/bb-switch';
import { BriefService } from '@/app/services/brief/brief.service';
import { MbaIcon } from '../../assignment/atoms/mba-icon/mba-icon';
import {
  ageSummary,
  audienceSummary,
  formatFollowers,
  genderIds,
  PERSONA_FIELDS,
  personaDraft,
  PersonaDraft,
  PersonaField,
  sectionStatus,
} from '../briefing.helpers';
import { BriefingService } from '../briefing.service';
import { bf } from '../briefing-ui';
import { BriefingDrawer } from '../molecules/briefing-drawer/briefing-drawer';
import { BriefingSection } from '../molecules/briefing-section/briefing-section';

type AgeBracket = AddBriefDemographicsDto['age'][number];
type Range = { min: number; max: number };

const RANGE_LABELS = new Map(FOLLOWER_RANGES.map((r) => [`${r.min}-${r.max}`, r.label]));

/**
 * Who Can Apply: the brief's demographics plus the "Ideal creator" persona. Keeps the stored
 * multi-select data (several countries, age brackets, follower bands). Location, gender and 18+
 * decide who sees the brief; the rest guide who the brand hires and AutoPilot.
 */
@Component({
  selector: 'mbt-briefing-criteria',
  imports: [BriefingSection, BriefingDrawer, MbaIcon, BbSwitch],
  template: `
    @if (svc.brief(); as brief) {
    <mbt-briefing-section key="crit" icon="users" title="Who Can Apply" [flash]="flash()"
      helper="Location, gender and 18+ decide which creators see this brief. The rest describe who you’d like to hire."
      [status]="status()" (edit)="open()">
      @if (status() === 'missing') {
        <div [class]="bf.empty">
          <span class="flex min-w-0 flex-1 flex-col gap-0.5">
            <b [class]="bf.emptyTitle">No rules set, so any SHOUT creator can apply</b>
            <span [class]="bf.sub">Add niches, location, age or audience size to get applicants who fit your brand.</span>
          </span>
          <button type="button" [class]="bf.btnPrimary" (click)="open()"><mba-icon name="plus" size="size-4" />Set up</button>
        </div>
      } @else {
        <dl class="m-0 grid grid-cols-3 gap-px overflow-hidden rounded-lg border border-(--border-subtle) bg-(--border-subtle) max-[1100px]:grid-cols-2 max-[640px]:grid-cols-1">
          <div [class]="cell">
            <dt [class]="bf.k">Niches</dt>
            <dd class="m-0">
              @if (niches().length) {
                <span class="flex flex-wrap gap-1.5">@for (n of niches(); track n) { <span [class]="bf.tag">{{ n }}</span> }</span>
              } @else { <b [class]="valueAny">Any niche</b> }
            </dd>
          </div>
          <div [class]="cell"><dt [class]="bf.k">Audience size</dt><dd class="m-0"><b [class]="audience() ? value : valueAny">{{ audience() ?? 'Any size' }}</b></dd></div>
          <div [class]="cell"><dt [class]="bf.k">Location</dt><dd class="m-0"><b [class]="location() ? value : valueAny">{{ location() ?? 'Anywhere' }}</b></dd></div>
          <div [class]="cell"><dt [class]="bf.k">Age</dt><dd class="m-0"><b [class]="age() ? value : valueAny">{{ age() ?? 'Any age' }}</b></dd></div>
          <div [class]="cell"><dt [class]="bf.k">Gender</dt><dd class="m-0"><b [class]="gender() ? value : valueAny">{{ gender() ?? 'Any gender' }}</b></dd></div>
          <div [class]="cell"><dt [class]="bf.k">18+ only</dt><dd class="m-0"><b [class]="adults() ? value : valueAny">{{ adults() ? 'Yes' : 'No' }}</b></dd></div>
        </dl>
        @if (byChannel().length) {
          <div [class]="bf.info"><mba-icon name="users" size="size-4" />
            <span>Audience by channel: @for (c of byChannel(); track c.channel) { <b>{{ c.channel }}</b> {{ c.range }}{{ $last ? '' : ' · ' }} }</span></div>
        }
        @if (personaRows().length) {
          <div class="overflow-hidden rounded-lg border border-(--border-subtle)">
            <div class="flex items-center gap-3 border-b border-(--border-subtle) bg-(--bg-alt) px-4 py-3">
              <span class="grid size-8 place-items-center rounded-full bg-(--app-accent-soft) text-(--app-accent-strong)"><mba-icon name="user-check" size="size-4" /></span>
              <span class="flex flex-col">
                <b class="font-(family-name:--font-display) text-[19px] leading-none font-extrabold">Ideal creator</b>
                <span [class]="bf.sub">The kind of creator you’re looking for</span>
              </span>
            </div>
            <dl class="m-0 grid grid-cols-[150px_1fr] max-[640px]:grid-cols-1">
              @for (row of personaRows(); track row.label) {
                <dt class="border-b border-(--border-subtle) px-4 py-2.5 text-[13px] text-(--fg-3) last-of-type:border-0">{{ row.label }}</dt>
                <dd class="m-0 border-b border-(--border-subtle) px-4 py-2.5 text-sm leading-[1.45] text-(--fg-1) last-of-type:border-0">{{ row.value }}</dd>
              }
            </dl>
          </div>
        }
      }
    </mbt-briefing-section>

    @if (editing()) {
    <mbt-briefing-drawer icon="users" title="Who Can Apply" [mode]="status() === 'missing' ? 'Set up' : 'Edit'"
      helper="Location, gender and 18+ decide which creators see this brief. The rest describe who you’d like to hire."
      [saving]="saving()" (closed)="editing.set(false)" (save)="save()">
      <div [class]="bf.field" role="group" aria-labelledby="bf-niches">
        <span><b id="bf-niches" [class]="bf.fieldLabel">Niches</b> <span [class]="bf.fieldSub">· Creators who post about these</span></span>
        <div class="flex flex-wrap gap-1.5">
          @for (interest of interestOptions; track interest.id) {
            <button type="button" [attr.aria-pressed]="hasInterest(interest.id)"
              [class]="hasInterest(interest.id) ? bf.chipOn : bf.chipOff" (click)="toggleInterest(interest.id)">{{ interest.name }}</button>
          }
        </div>
      </div>
      <div [class]="bf.field" role="group" aria-labelledby="bf-audience">
        <span><b id="bf-audience" [class]="bf.fieldLabel">Audience size</b> <span [class]="bf.fieldSub">· Followers on their largest account. Pick none for any size</span></span>
        <div class="flex flex-wrap gap-1.5">
          @for (range of rangeOptions; track range.label) {
            <button type="button" [attr.aria-pressed]="hasRange(range)" [class]="hasRange(range) ? bf.chipOn : bf.chipOff" (click)="toggleRange(range)">{{ range.label }}</button>
          }
        </div>
      </div>
      <div [class]="bf.field" role="group" aria-labelledby="bf-location">
        <span><b id="bf-location" [class]="bf.fieldLabel">Location</b> <span [class]="bf.fieldSub">· Only creators based here see the brief. Pick none for anywhere</span></span>
        <div class="flex flex-wrap gap-1.5">
          @for (country of countryOptions; track country.countryCode) {
            <button type="button" [attr.aria-pressed]="countries().includes(country.countryCode)"
              [class]="countries().includes(country.countryCode) ? bf.chipOn : bf.chipOff" (click)="toggleCountry(country.countryCode)">{{ country.countryName }}</button>
          }
        </div>
      </div>
      <div [class]="bf.field" role="group" aria-labelledby="bf-age">
        <span><b id="bf-age" [class]="bf.fieldLabel">Age</b> <span [class]="bf.fieldSub">· Pick none for any age</span></span>
        <div class="flex flex-wrap gap-1.5">
          @for (bracket of ageOptions; track bracket.id) {
            <button type="button" [attr.aria-pressed]="hasAge(bracket)" [disabled]="adultsOnly() && bracket.minAge < 18"
              [class]="(hasAge(bracket) ? bf.chipOn : bf.chipOff) + ' disabled:cursor-not-allowed disabled:opacity-40'" (click)="toggleAge(bracket)">{{ bracket.label }}</button>
          }
        </div>
      </div>
      <div [class]="bf.field" role="group" aria-labelledby="bf-gender">
        <span><b id="bf-gender" [class]="bf.fieldLabel">Gender</b> <span [class]="bf.fieldSub">· Only creators who match see the brief</span></span>
        <div class="flex flex-wrap gap-1.5">
          <button type="button" [attr.aria-pressed]="!genders().length" [class]="!genders().length ? bf.chipOn : bf.chipOff" (click)="genders.set([])">Any</button>
          @for (option of genderOptions; track option.id) {
            <button type="button" [attr.aria-pressed]="genders().includes(option.id)"
              [class]="genders().includes(option.id) ? bf.chipOn : bf.chipOff" (click)="toggleGender(option.id)">{{ option.name }}</button>
          }
        </div>
      </div>
      <div [class]="bf.field">
        <span><b [class]="bf.fieldLabel">18+ only</b> <span [class]="bf.fieldSub">· Creators must be 18 or over, whatever the age range</span></span>
        <span class="flex items-center gap-3 text-sm font-semibold">
          <bb-switch [checked]="adultsOnly()" ariaLabel="18+ only" (checkedChange)="setAdultsOnly($event)" />
          {{ adultsOnly() ? 'On · only creators aged 18 or over can apply' : 'Off' }}
        </span>
      </div>
      <div [class]="bf.info"><mba-icon name="info" size="size-4" /><span>Tighter rules mean fewer, better-matched applicants.</span></div>

      <div class="flex flex-col gap-1 border-t border-(--border-subtle) pt-4">
        <b class="font-(family-name:--font-display) text-xl leading-none font-extrabold">Ideal creator</b>
        <span [class]="bf.fieldSub">Describe who you want to apply. All optional</span>
      </div>
      @for (field of personaFields; track field.key) {
        <label [class]="bf.field">
          <b [class]="bf.fieldLabel">{{ field.label }}</b>
          <textarea [class]="bf.input + ' min-h-16 resize-y'" maxlength="1000" [value]="persona()[field.key]" (input)="setPersona(field.key, $event)"></textarea>
        </label>
      }
    </mbt-briefing-drawer>
    }
    }
  `,
  host: { style: 'display: contents' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BriefingCriteria {
  protected readonly bf = bf;
  protected readonly cell = 'flex flex-col gap-2 bg-(--mba-surface) px-4 py-3.5';
  protected readonly value = 'font-(family-name:--font-display) text-xl leading-tight font-extrabold text-(--fg-1)';
  protected readonly valueAny = 'font-(family-name:--font-display) text-xl leading-tight font-extrabold text-(--fg-muted)';
  protected readonly personaFields = PERSONA_FIELDS;
  protected readonly rangeOptions = FOLLOWER_RANGES;
  protected readonly svc = inject(BriefingService);
  private readonly lookups = inject(BriefService);
  protected readonly interestOptions = this.lookups.creatorInterests;
  protected readonly countryOptions = this.lookups.countryCodes;
  protected readonly ageOptions = this.lookups.creatorAges;
  protected readonly genderOptions = this.lookups.creatorGenders.filter((g) => g.id !== 0);

  readonly flash = input(false);

  protected readonly status = computed(() => sectionStatus('crit', this.svc.brief()!));
  private readonly demo = computed(() => this.svc.brief()?.demographics);

  protected readonly adults = computed(() => !!this.demo()?.is18plus);
  protected readonly niches = computed(() =>
    (this.demo()?.interests ?? []).map(Number).flatMap((id) => {
      const match = this.interestOptions.find((i) => Number(i.id) === id);
      return match ? [match.name] : [];
    }),
  );
  protected readonly audience = computed(() => audienceSummary(this.demo()?.followerRanges ?? [], RANGE_LABELS));
  protected readonly location = computed(() => {
    const names = (this.demo()?.countryCodes ?? []).map(
      (code) => this.countryOptions.find((c) => c.countryCode === code)?.countryName.replace(/^\S+\s/, '') ?? code,
    );
    const all = [...names, ...(this.demo()?.location ?? [])];
    return all.length ? all.join(', ') : null;
  });
  protected readonly age = computed(() => ageSummary(this.demo()?.age ?? []));
  protected readonly gender = computed(() => {
    const ids = genderIds(this.demo());
    return ids.length ? ids.map((id) => this.genderOptions.find((g) => g.id === id)?.name ?? '').filter(Boolean).join(', ') : null;
  });
  protected readonly byChannel = computed(() => {
    const byChannel = this.demo()?.influencerLevelByChannel;
    const rows: { channel: string; range: string }[] = [];
    const add = (channel: string, level?: { followersFrom: number; followersTo: number } | null) => {
      if (level) rows.push({ channel, range: `${formatFollowers(level.followersFrom)}–${formatFollowers(level.followersTo)}` });
    };
    add('TikTok', byChannel?.tiktok);
    add('Instagram', byChannel?.instagram);
    add('YouTube', byChannel?.youtube);
    return rows;
  });
  protected readonly personaRows = computed(() => {
    const persona = this.svc.brief()?.creatorPersona;
    return PERSONA_FIELDS.flatMap(({ key, label }) => (persona?.[key]?.trim() ? [{ label, value: persona[key] }] : []));
  });

  protected readonly editing = signal(false);
  protected readonly saving = signal(false);
  protected readonly interests = signal<number[]>([]);
  protected readonly ranges = signal<Range[]>([]);
  protected readonly countries = signal<string[]>([]);
  protected readonly ages = signal<AgeBracket[]>([]);
  protected readonly genders = signal<number[]>([]);
  protected readonly adultsOnly = signal(false);
  protected readonly persona = signal<PersonaDraft>(personaDraft(null));

  open(): void {
    const demo = this.demo();
    this.interests.set((demo?.interests ?? []).map(Number));
    this.ranges.set([...(demo?.followerRanges ?? [])]);
    this.countries.set([...(demo?.countryCodes ?? [])]);
    this.ages.set([...(demo?.age ?? [])]);
    this.genders.set(genderIds(demo));
    this.adultsOnly.set(!!demo?.is18plus);
    this.persona.set(personaDraft(this.svc.brief()?.creatorPersona));
    this.editing.set(true);
  }

  private toggleIn<T>(list: T[], item: T, same: (a: T, b: T) => boolean = (a, b) => a === b): T[] {
    return list.some((x) => same(x, item)) ? list.filter((x) => !same(x, item)) : [...list, item];
  }

  protected hasInterest(id: number | string): boolean {
    return this.interests().includes(Number(id));
  }

  protected toggleInterest(id: number | string): void {
    this.interests.update((list) => this.toggleIn(list, Number(id)));
  }

  protected hasRange(range: Range): boolean {
    return this.ranges().some((r) => r.min === range.min && r.max === range.max);
  }

  protected toggleRange(range: Range): void {
    this.ranges.update((list) =>
      this.toggleIn(list, { min: range.min, max: range.max }, (a, b) => a.min === b.min && a.max === b.max),
    );
  }

  protected toggleCountry(code: string): void {
    this.countries.update((list) => this.toggleIn(list, code));
  }

  protected hasAge(bracket: AgeBracket): boolean {
    return this.ages().some((a) => a.id === bracket.id);
  }

  protected toggleAge(bracket: AgeBracket): void {
    this.ages.update((list) => this.toggleIn(list, bracket, (a, b) => a.id === b.id));
  }

  protected toggleGender(id: number): void {
    this.genders.update((list) => this.toggleIn(list, id));
  }

  /** 18+ drops any bracket that starts under 18, as the old editor did. */
  protected setAdultsOnly(on: boolean): void {
    this.adultsOnly.set(on);
    if (on) {
      this.ages.update((list) => list.filter((a) => a.minAge >= 18));
    }
  }

  protected setPersona(key: PersonaField, event: Event): void {
    const value = (event.target as HTMLTextAreaElement).value;
    this.persona.update((p) => ({ ...p, [key]: value }));
  }

  protected async save(): Promise<void> {
    const demo = this.demo();
    const payload: AddBriefDemographicsDto = {
      gender: this.genders(),
      interests: this.interests(),
      location: [...(demo?.location ?? [])],
      age: this.ages(),
      categoryId: demo?.categoryId ?? 3,
      subCategoryId: demo?.subCategoryId ?? 54,
      countryCodes: this.countries(),
      is18plus: this.adultsOnly(),
      influencerType: demo?.influencerType?.length ? demo.influencerType.map(Number) : [InfluencerTypeEnum.Any],
      followerRanges: this.ranges(),
    };
    const saved = personaDraft(this.svc.brief()?.creatorPersona);
    const personaChanged = PERSONA_FIELDS.some(({ key }) => this.persona()[key].trim() !== saved[key].trim());
    this.saving.set(true);
    const ok = await this.svc.save(
      [() => this.svc.demographics(payload), ...(personaChanged ? [() => this.svc.creatorPersona(this.persona())] : [])],
      'Who Can Apply saved',
    );
    this.saving.set(false);
    if (ok) {
      this.editing.set(false);
    }
  }
}
